import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

// @mui
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import TableHeader from 'components/Table/TableHeader';

import { mockSearchProducts } from './mockApi';

const HEADERS = [
  { id: 'pd.image_id', label: 'รูป', align: 'center', width: 60, sort: false },
  { id: 'pd.barcode', label: 'Barcode', align: 'left', width: 140, sort: false },
  { id: 'pd.name', label: 'สินค้า', align: 'left', sort: false },
  { id: 'pd.unit_name', label: 'หน่วย', align: 'center', width: 80, sort: false },
  { id: 'action', label: 'เพิ่ม', align: 'center', width: 70, sort: false },
];

function CountingSearchProduct({ open, onClose, onAdd, selectedIds, filterIds }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageTotal, setPageTotal] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const PER_PAGE = 10;

  const load = useCallback(
    (s, p) => {
      setLoading(true);
      mockSearchProducts({
        search: s,
        page: p,
        per_page: PER_PAGE,
        barcode_ids: filterIds?.length ? filterIds : undefined,
      }).then((result) => {
        setProducts(result.data ?? []);
        setPageTotal(result.total_pages ?? 0);
        setLoading(false);
      });
    },
    [filterIds]
  );

  useEffect(() => {
    if (open) {
      setSearch('');
      setPage(1);
      load('', 1);
    }
  }, [open]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load(search, 1);
  };

  const handlePage = (e, p) => {
    setPage(p);
    load(search, p);
  };

  return (
    <Dialog open={open} maxWidth='sm' fullWidth keepMounted>
      <DialogTitle>
        ค้นหาสินค้าเพื่อเพิ่มในใบนับ
        {filterIds?.length > 0 && (
          <Chip
            label={`จำกัดจากใบนับหลัก ${filterIds.length} รายการ`}
            size='small'
            color='info'
            sx={{ ml: 1 }}
          />
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (th) => th.palette.grey[500] }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <MDBox component='form' noValidate onSubmit={handleSearch} mb={2}>
          <MDInput
            fullWidth
            value={search}
            type='search'
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position='start'>ค้นหา :</InputAdornment>,
              endAdornment: loading ? (
                <InputAdornment position='end'>
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
              autoComplete: 'off',
            }}
          />
        </MDBox>

        <TableContainer sx={{ maxHeight: 380 }}>
          <Table size='small' stickyHeader>
            <TableHeader order='asc' orderBy='' onRequestSort={() => {}} data={HEADERS} />
            <TableBody>
              {!loading && products.length === 0 && (
                <TableRow>
                  <TableCell colSpan={HEADERS.length} align='center' sx={{ py: 4 }}>
                    <MDTypography variant='caption' color='secondary'>
                      ไม่พบสินค้า
                    </MDTypography>
                  </TableCell>
                </TableRow>
              )}
              {products.map((p) => {
                const already = selectedIds?.includes(p.barcode_id);
                return (
                  <TableRow key={p.barcode_id} hover>
                    <TableCell align='center'>
                      <Avatar
                        src={p.image_url}
                        variant='rounded'
                        sx={{ width: 32, height: 32, mx: 'auto' }}
                      >
                        <Icon>inventory_2</Icon>
                      </Avatar>
                    </TableCell>
                    <TableCell align='left'>
                      <MDTypography variant='caption' fontWeight='medium'>
                        {p.barcode}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='left'>
                      <MDTypography variant='caption'>{p.name}</MDTypography>
                    </TableCell>
                    <TableCell align='center'>
                      <MDTypography variant='caption' color='secondary'>
                        {p.unit_name}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='center'>
                      {already ? (
                        <Chip label='เพิ่มแล้ว' size='small' color='success' />
                      ) : (
                        <Tooltip title='เพิ่มสินค้า'>
                          <IconButton size='small' color='info' onClick={() => onAdd(p)}>
                            <Icon>add_circle_outline</Icon>
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {pageTotal > 1 && (
          <MDBox display='flex' justifyContent='center' mt={2}>
            <Pagination
              count={pageTotal}
              page={page}
              onChange={handlePage}
              size='small'
              color='secondary'
              showFirstButton
              showLastButton
            />
          </MDBox>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton variant='text' color='secondary' onClick={onClose}>
          <Icon>close</Icon>&nbsp;ปิด
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

CountingSearchProduct.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  filterIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

CountingSearchProduct.defaultProps = {
  selectedIds: [],
  filterIds: [],
};

export default CountingSearchProduct;
